import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { toFormatPrice, toUnformatPrice } from '../../common/helpers/function';
import newCatalogSearchClearIcon from '../../../assets/images/newcatalogsearchclearicon.svg';
import {
  DEFAULT_CATALOG_FILTERS,
  countCatalogFilterSelections,
  getFilterCountExcludeKeys,
} from './catalogAllFilterUtils';
import './CatalogAllFilterPanel.scss';

const CHIP_SECTIONS = [
  { key: 'item', label: 'Item', dataKey: 'item', idKey: '_id' },
  { key: 'collection', label: 'Collection', dataKey: 'collection', idKey: '_id' },
  { key: 'metal', label: 'Metal', dataKey: 'metal', idKey: '_id' },
  { key: 'stone', label: 'Stone', dataKey: 'stone', idKey: '_id' },
  { key: 'size', label: 'Size', dataKey: 'size', idKey: '_id' },
  { key: 'status', label: 'Status', dataKey: 'status', idKey: 'value' },
];

const CatalogAllFilterPanel = ({
  open,
  onClose,
  draft,
  onDraftChange,
  onApply,
  onClear,
  filterData = null,
  defaultSectionSelections = {},
  categoryId = '',
}) => {
  const { t } = useTranslation();
  const [priceMinText, setPriceMinText] = useState('');
  const [priceMaxText, setPriceMaxText] = useState('');
  const priceMinFocusedRef = useRef(false);
  const priceMaxFocusedRef = useRef(false);
  const [expanded, setExpanded] = useState({
    sort: true,
    item: true,
    collection: true,
    metal: false,
    stone: false,
    size: false,
    status: false,
    price: false,
  });

  useEffect(() => {
    if (!open || !filterData?.Price) return;
    if (!draft.price) {
      onDraftChange({
        ...draft,
        price: [0, filterData.Price.max],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filterData]);

  const formatPriceDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return toFormatPrice(value);
  };

  useEffect(() => {
    if (!open || !draft.price?.length) return;
    if (!priceMinFocusedRef.current) {
      setPriceMinText(formatPriceDisplay(draft.price[0]));
    }
    if (!priceMaxFocusedRef.current) {
      setPriceMaxText(formatPriceDisplay(draft.price[1]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, draft.price?.[0], draft.price?.[1]]);

  if (!open) return null;

  const toggleExpanded = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleChip = (sectionKey, idKey, value) => {
    const current = draft[sectionKey] || [];
    const next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    onDraftChange({ ...draft, [sectionKey]: next });
  };

  const selectAllChips = (sectionKey, dataKey, idKey) => {
    const list = filterData?.[dataKey] || [];
    const allIds = list
      .map((item) => item[idKey])
      .filter(Boolean);
    onDraftChange({ ...draft, [sectionKey]: allIds });
  };

  const clearSection = (sectionKey) => {
    const defaultValues = defaultSectionSelections[sectionKey];
    onDraftChange({
      ...draft,
      [sectionKey]:
        defaultValues?.length > 0 ? [...defaultValues] : [],
    });
  };

  const handleSortSelect = (value) => {
    onDraftChange({
      ...draft,
      sortBy: draft.sortBy === value ? '' : value,
    });
  };

  const priceBounds = {
    min: 0,
    max: filterData?.Price?.max ?? 1000,
  };

  const updatePriceRange = (minVal, maxVal) => {
    const min = priceBounds.min;
    const max = priceBounds.max;
    let low = Math.min(max, Math.max(min, Number(minVal) ?? min));
    let high = Math.min(max, Math.max(min, Number(maxVal) ?? max));
    if (low > high) {
      [low, high] = [high, low];
    }
    onDraftChange({ ...draft, price: [low, high] });
  };

  const handlePriceInputChange = (index, value) => {
    if (index === 0) {
      setPriceMinText(value);
      return;
    }
    setPriceMaxText(value);
  };

  const commitPriceInput = (index) => {
    const rawValue = index === 0 ? priceMinText : priceMaxText;
    const parsed = toUnformatPrice(rawValue);
    const fallback =
      index === 0 ? draft.price?.[0] ?? priceBounds.min : draft.price?.[1] ?? priceBounds.max;
    const num =
      rawValue.trim() === '' || !Number.isFinite(parsed) ? fallback : parsed;

    if (index === 0) {
      updatePriceRange(num, draft.price?.[1] ?? priceBounds.max);
      return;
    }
    updatePriceRange(draft.price?.[0] ?? priceBounds.min, num);
  };

  const handlePriceSliderChange = (value) => {
    onDraftChange({ ...draft, price: value });
    if (!priceMinFocusedRef.current) {
      setPriceMinText(formatPriceDisplay(value[0]));
    }
    if (!priceMaxFocusedRef.current) {
      setPriceMaxText(formatPriceDisplay(value[1]));
    }
  };

  const filterCountExcludeKeys = getFilterCountExcludeKeys(categoryId);

  const activeFilterCount = countCatalogFilterSelections(draft, {
    priceBounds: filterData?.Price,
    excludeKeys: filterCountExcludeKeys,
  });

  const renderSectionHead = (sectionKey, label, { count = 0, selectAllProps = null } = {}) => {
    const isOpen = expanded[sectionKey];
    return (
      <div className="catalog-all-filter-panel__section-head" role="presentation">
        <button
          type="button"
          className="catalog-all-filter-panel__section-head-toggle"
          onClick={() => toggleExpanded(sectionKey)}
          aria-expanded={isOpen}
        >
          <span className="catalog-all-filter-panel__section-head-left">
            <span className="catalog-all-filter-panel__section-title">{t(label)}</span>
            {count > 0 ? (
              <span className="catalog-all-filter-panel__section-count">({count})</span>
            ) : null}
          </span>
          <span
            className={
              isOpen
                ? 'catalog-all-filter-panel__chevron catalog-all-filter-panel__chevron--up'
                : 'catalog-all-filter-panel__chevron'
            }
            aria-hidden
          />
        </button>
        {selectAllProps?.canSelectAll ? (
          <button
            type="button"
            className="catalog-all-filter-panel__select-all"
            onClick={selectAllProps.onSelectAll}
          >
            {t('select all')}
          </button>
        ) : null}
        {selectAllProps?.canClear ? (
          <button
            type="button"
            className="catalog-all-filter-panel__select-all"
            onClick={selectAllProps.onClear}
          >
            {t('Clear')}
          </button>
        ) : null}
      </div>
    );
  };

  const renderChipSection = ({ key, label, dataKey, idKey }) => {
    const options = filterData?.[dataKey] || [];
    if (!options.length) return null;
    const selected = draft[key] || [];
    const sectionCount = selected.length;
    const defaultCount = defaultSectionSelections[key]?.length || 0;
    const isOpen = expanded[key];

    return (
      <div className="catalog-all-filter-panel__section" key={key}>
        {renderSectionHead(key, label, {
          count: sectionCount,
          selectAllProps: isOpen
            ? {
                canSelectAll: sectionCount < options.length,
                canClear: sectionCount > defaultCount,
                onSelectAll: () => selectAllChips(key, dataKey, idKey),
                onClear: () => clearSection(key),
              }
            : null,
        })}
        {isOpen ? (
          <div className="catalog-all-filter-panel__section-body">
            <div className="catalog-all-filter-panel__chips">
              {options.map((option) => {
                const value = option[idKey];
                const name = option?.name;
                const active = selected.includes(value);
                return (
                  <button
                    type="button"
                    key={value}
                    className={
                      active
                        ? 'catalog-all-filter-panel__chip catalog-all-filter-panel__chip--active'
                        : 'catalog-all-filter-panel__chip'
                    }
                    onClick={() => toggleChip(key, idKey, value)}
                  >
                    {name}
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <>
      <div
        className="catalog-all-filter-backdrop"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="catalog-all-filter-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t('Filter and Sort')}
      >
        <div className="catalog-all-filter-panel__close-row">
          <button
            type="button"
            className="catalog-all-filter-panel__close-btn"
            onClick={onClose}
            aria-label={t('Close')}
          >
            <img src={newCatalogSearchClearIcon} alt="" />
          </button>
        </div>

        <div className="catalog-all-filter-panel__header">
          <div className="catalog-all-filter-panel__title-wrap">
            <span className="catalog-all-filter-panel__title">
              {t('Filter and Sort')}
            </span>
            {activeFilterCount > 0 ? (
              <span className="catalog-all-filter-panel__title-count">
                ({activeFilterCount})
              </span>
            ) : null}
          </div>
          <button
            type="button"
            className="catalog-all-filter-panel__clear-link"
            onClick={onClear}
          >
            {t('Clear filters')}
          </button>
        </div>

        <div className="catalog-all-filter-panel__body">
          <div className="catalog-all-filter-panel__section">
            {renderSectionHead('sort', 'Sort by')}
            {expanded.sort ? (
              <div className="catalog-all-filter-panel__section-body">
                <ul className="catalog-all-filter-panel__sort-list">
                  {(filterData?.sort_by || []).map((opt) => (
                    <li key={opt.value}>
                      <button
                        type="button"
                        className="catalog-all-filter-panel__sort-item"
                        onClick={() => handleSortSelect(opt.value)}
                      >
                        <span
                          className={
                            draft.sortBy === opt.value
                              ? 'catalog-all-filter-panel__radio catalog-all-filter-panel__radio--checked'
                              : 'catalog-all-filter-panel__radio'
                          }
                          aria-hidden
                        />
                        {opt.name}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          {CHIP_SECTIONS.map(renderChipSection)}

          {filterData?.Price ? (
            <div className="catalog-all-filter-panel__section catalog-all-filter-panel__section--price">
              {renderSectionHead('price', 'Price')}
              {expanded.price && draft.price ? (
                <div className="catalog-all-filter-panel__section-body">
                  <div className="catalog-all-filter-panel__price-inner">
                    <div className="catalog-all-filter-panel__price-slider">
                      <Slider
                        range
                        min={priceBounds.min}
                        max={priceBounds.max}
                        value={draft.price}
                        onChange={handlePriceSliderChange}
                      />
                    </div>
                    <div className="catalog-all-filter-panel__price-inputs">
                      <div className="catalog-all-filter-panel__price-field">
                      <label
                        className="catalog-all-filter-panel__price-label"
                        htmlFor="catalog-all-min-price"
                      >
                        {t('Min price')}:
                      </label>
                      <input
                        id="catalog-all-min-price"
                        type="text"
                        className="catalog-all-filter-panel__price-input"
                        value={priceMinText}
                        onFocus={() => {
                          priceMinFocusedRef.current = true;
                        }}
                        onBlur={() => {
                          priceMinFocusedRef.current = false;
                          commitPriceInput(0);
                        }}
                        onChange={(e) =>
                          handlePriceInputChange(0, e.target.value)
                        }
                      />
                      </div>
                      <div className="catalog-all-filter-panel__price-field">
                        <label
                          className="catalog-all-filter-panel__price-label"
                          htmlFor="catalog-all-max-price"
                        >
                          {t('Max price')}:
                        </label>
                        <input
                          id="catalog-all-max-price"
                          type="text"
                          className="catalog-all-filter-panel__price-input"
                          value={priceMaxText}
                          onFocus={() => {
                            priceMaxFocusedRef.current = true;
                          }}
                          onBlur={() => {
                            priceMaxFocusedRef.current = false;
                            commitPriceInput(1);
                          }}
                          onChange={(e) =>
                            handlePriceInputChange(1, e.target.value)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="catalog-all-filter-panel__footer">
          <button
            type="button"
            className="catalog-all-filter-panel__apply-btn"
            onClick={() => onApply(draft)}
          >
            {t('Apply Filters')}
          </button>
        </div>
      </aside>
    </>
  );
};

export default CatalogAllFilterPanel;
